import { useState, useMemo, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, X, Pencil } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

const STATUS_OPTIONS = ['active', 'inactive', 'lost', 'cancelled'];

const STATUS_COLORS = {
  active:    { bg: '#F0FDF4', color: '#16A34A' },
  inactive:  { bg: '#F3F4F6', color: '#6B7280' },
  lost:      { bg: '#FEF3C7', color: '#92400E' },
  cancelled: { bg: '#FEE2E2', color: '#B91C1C' },
};

const emptyForm = { card_id: '', vehicle_id: '', provider: '', cost_center_id: '', card_status: 'active', notes: '' };

function toEditForm(m) {
  return {
    vehicle_id:     m.vehicle_id || '',
    provider:       m.provider || '',
    cost_center_id: m.cost_center_id || '',
    card_status:    m.card_status,
    notes:          m.notes || '',
  };
}

export default function FuelCardMapping() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [statusFilter, setStatusFilter] = useState('active');
  const [providerFilter, setProviderFilter] = useState('all');
  const [ccFilter, setCcFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const { data: mappings = [], isLoading } = useQuery({
    queryKey: ['fuel-cards'],
    queryFn:  () => api.get('/fuel-cards'),
  });

  const { data: costCenters = [] } = useQuery({
    queryKey: ['admin-overtime-cost-centers'],
    queryFn:  () => api.get('/admin/overtime-cost-centers'),
  });

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/fuel-cards', payload),
    onSuccess: () => {
      toast.success('Fuel card mapping added.');
      qc.invalidateQueries(['fuel-cards']);
      setForm(emptyForm);
      setShowAdd(false);
    },
    onError: (err) => toast.error(err.message || 'Failed to add mapping.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/fuel-cards/${id}`, payload),
    onSuccess: () => {
      toast.success('Mapping updated.');
      qc.invalidateQueries(['fuel-cards']);
      setEditingId(null);
    },
    onError: (err) => toast.error(err.message || 'Failed to update mapping.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/fuel-cards/${id}`),
    onSuccess: () => {
      toast.success('Mapping removed.');
      qc.invalidateQueries(['fuel-cards']);
    },
    onError: (err) => toast.error(err.message || 'Failed to remove mapping.'),
  });

  function handleCreate(e) {
    e.preventDefault();
    if (!form.card_id.trim()) {
      toast.error('Card ID is required.');
      return;
    }
    createMutation.mutate({
      ...form,
      vehicle_id: form.vehicle_id.trim() || null,
      provider: form.provider.trim() || null,
      cost_center_id: form.cost_center_id || null,
    });
  }

  function startEdit(m) {
    setEditingId(m.id);
    setEditForm(toEditForm(m));
  }

  function handleSaveEdit(id) {
    updateMutation.mutate({
      id,
      payload: {
        ...editForm,
        vehicle_id: editForm.vehicle_id.trim() || null,
        provider: editForm.provider.trim() || null,
        cost_center_id: editForm.cost_center_id || null,
      },
    });
  }

  const providers = useMemo(
    () => [...new Set(mappings.map((m) => m.provider).filter(Boolean))].sort(),
    [mappings]
  );

  if (isLoading) return <LoadingSpinner />;

  const searchNorm = search.trim().toLowerCase();
  const visibleMappings = mappings.filter((m) => {
    if (statusFilter !== 'all' && m.card_status !== statusFilter) return false;
    if (providerFilter !== 'all' && (m.provider || '') !== providerFilter) return false;
    if (ccFilter === 'unassigned' && m.cost_center_id) return false;
    if (ccFilter !== 'all' && ccFilter !== 'unassigned' && m.cost_center_id !== ccFilter) return false;
    if (searchNorm) {
      const hay = `${m.card_id} ${m.vehicle_id || ''}`.toLowerCase();
      if (!hay.includes(searchNorm)) return false;
    }
    return true;
  });

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Fuel Card Mapping"
        subtitle="Map each fuel card to the vehicle and cost center it belongs to. Incoming fuel statements resolve their cost center from this table."
      />

      <div style={s.actionsRow}>
        <div style={s.filterRow}>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Status</label>
            <select style={s.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Active</option>
              <option value="all">All</option>
              {STATUS_OPTIONS.filter((st) => st !== 'active').map((st) => (
                <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>
              ))}
            </select>
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Provider</label>
            <select style={s.filterSelect} value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
              <option value="all">All</option>
              {providers.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Cost Center</label>
            <select style={s.filterSelect} value={ccFilter} onChange={(e) => setCcFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="unassigned">Unassigned</option>
              {costCenters.map((cc) => (
                <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
              ))}
            </select>
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Search</label>
            <input
              style={s.filterSelect}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Card or vehicle ID…"
            />
          </div>
          <span style={s.filterCount}>{visibleMappings.length} of {mappings.length}</span>
        </div>
        <button style={s.addBtn} onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? <X size={13} /> : <Plus size={13} />}
          {showAdd ? 'Cancel' : 'Add Mapping'}
        </button>
      </div>

      {showAdd && (
        <form style={s.addCard} onSubmit={handleCreate}>
          <div style={s.formGrid}>
            <div style={s.field}>
              <label style={s.label}>Card ID</label>
              <input style={s.input} value={form.card_id} onChange={(e) => setForm({ ...form, card_id: e.target.value })} placeholder="e.g. 4001-2233" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Vehicle ID</label>
              <input style={s.input} value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} placeholder="Optional — e.g. UNIT-118" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Provider</label>
              <input style={s.input} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="e.g. Voyager" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Cost Center</label>
              <select style={s.input} value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}>
                <option value="">Unassigned</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Status</label>
              <select style={s.input} value={form.card_status} onChange={(e) => setForm({ ...form, card_status: e.target.value })}>
                {STATUS_OPTIONS.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
            <div style={{ ...s.field, gridColumn: '1 / -1' }}>
              <label style={s.label}>Notes</label>
              <input style={s.input} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <button type="submit" style={s.saveBtn} disabled={createMutation.isPending}>
            <Save size={13} />
            {createMutation.isPending ? 'Saving…' : 'Save Mapping'}
          </button>
        </form>
      )}

      <div style={s.card}>
        <div style={{ overflowX: 'auto' }}>
          {/* Wide table: it scrolls inside this, so the page does not. */}
        <table style={s.table}>
          <thead>
            <tr>
              {['Card ID', 'Vehicle', 'Provider', 'Cost Center', 'Status', 'Notes', 'Updated', ''].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleMappings.map((m) => {
              const st = STATUS_COLORS[m.card_status] || STATUS_COLORS.inactive;
              const isEditing = editingId === m.id;
              return (
                <Fragment key={m.id}>
                  <tr style={s.tr}>
                    <td style={s.td}><strong>{m.card_id}</strong></td>
                    <td style={s.td}>{m.vehicle_id || '—'}</td>
                    <td style={s.td}>{m.provider || '—'}</td>
                    <td style={s.td}>{m.cost_center_name ? `${m.cost_center_code} — ${m.cost_center_name}` : '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusPill, background: st.bg, color: st.color }}>{m.card_status}</span>
                    </td>
                    <td style={{ ...s.td, color: '#9CA3AF' }}>{m.notes || '—'}</td>
                    <td style={s.td}>{new Date(m.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        style={s.iconBtn}
                        title="Edit mapping"
                        onClick={() => (isEditing ? setEditingId(null) : startEdit(m))}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        style={s.deleteBtn}
                        title="Remove mapping"
                        onClick={() => {
                          if (window.confirm(`Remove the mapping for card ${m.card_id}?`)) deleteMutation.mutate(m.id);
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr style={s.editRow}>
                      <td colSpan={8} style={s.editTd}>
                        <div style={s.editGrid}>
                          <div style={s.field}>
                            <label style={s.label}>Vehicle ID</label>
                            <input style={s.input} value={editForm.vehicle_id} onChange={(e) => setEditForm({ ...editForm, vehicle_id: e.target.value })} />
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Provider</label>
                            <input style={s.input} value={editForm.provider} onChange={(e) => setEditForm({ ...editForm, provider: e.target.value })} />
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Cost Center</label>
                            <select style={s.input} value={editForm.cost_center_id} onChange={(e) => setEditForm({ ...editForm, cost_center_id: e.target.value })}>
                              <option value="">Unassigned</option>
                              {costCenters.map((cc) => (
                                <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                              ))}
                            </select>
                          </div>
                          <div style={s.field}>
                            <label style={s.label}>Status</label>
                            <select style={s.input} value={editForm.card_status} onChange={(e) => setEditForm({ ...editForm, card_status: e.target.value })}>
                              {STATUS_OPTIONS.map((st2) => <option key={st2} value={st2}>{st2}</option>)}
                            </select>
                          </div>
                          <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                            <label style={s.label}>Notes</label>
                            <input style={s.input} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
                          </div>
                        </div>
                        <div style={s.editActions}>
                          <button style={s.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
                          <button style={s.saveBtn} onClick={() => handleSaveEdit(m.id)} disabled={updateMutation.isPending}>
                            <Save size={13} />
                            {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!visibleMappings.length && (
              <tr><td colSpan={8} style={s.empty}>
                {mappings.length ? 'No fuel card mappings match this filter.' : 'No fuel card mappings yet.'}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:        { maxWidth: '1100px', margin: '0 auto' },
  actionsRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' },
  filterRow:   { display: 'flex', alignItems: 'flex-end', gap: '14px', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '10px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
  filterSelect: { padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '12px', color: '#374151', fontFamily: 'inherit', background: '#fff', minWidth: '140px' },
  filterCount: { fontSize: '11px', color: '#9CA3AF', paddingBottom: '7px' },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', fontSize: '12px', fontWeight: 600,
    border: 'none', borderRadius: '6px', background: '#1B2A4A',
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit', height: '31px',
  },
  addCard:     { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
  formGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' },
  editGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' },
  field:       {},
  label:       { display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:       { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', background: '#fff' },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', fontSize: '12px', fontWeight: 600,
    border: 'none', borderRadius: '6px', background: '#1B2A4A',
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  cancelBtn: {
    padding: '8px 16px', fontSize: '12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', background: '#fff', color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
  },
  card:        { ...card.base, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.th, ...tbl.th },
  tr:          { ...tbl.tr, ...tbl.tr },
  td:          { ...tbl.td, ...tbl.td },
  statusPill:  { display: 'inline-flex', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500, textTransform: 'capitalize' },
  iconBtn:     { ...btn.icon, marginRight: '4px' },
  deleteBtn:   { ...btn.iconDanger },
  editRow:     { borderBottom: '1px solid #F3F4F6', background: '#F9FAFB' },
  editTd:      { padding: '16px' },
  editActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  empty:       { textAlign: 'center', padding: '32px', color: '#9CA3AF' },
};
